/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from "react";
import {useTranslation} from "react-i18next";
import {RadioGroup} from "@headlessui/react";
import clsx from "clsx";
import {PackagesItemsPerson} from "types/entities/PackagesItems";
import {getDurationOptions, usePackageItemKartingOptions} from "utils/packageUtils";
import {PackageItemPersonType} from "types/enums/PackageItemPersonType";
import FeatherIcon from "components/FeatherIcon";
import {currencyNumberFormat} from "utils/utility";

interface BookingPackagesTicketsSelectStandardProps {
    chosenDuration: number | undefined;
    setChosenDuration: (val: number) => void;
    enabled: boolean;
    withKarting?: number;
    className?: string;
    personType: PackageItemPersonType;
    disabled2Hours: boolean;
    disabled1Hour: boolean;
    showLabels?: boolean;
}

const BookingPackagesTicketsSelectStandard = (props: BookingPackagesTicketsSelectStandardProps) => {
    const {t} = useTranslation();
    const {getPackageItemForCombination, getPackageItemPrice} = usePackageItemKartingOptions();
    const durationOptions = getDurationOptions(props.disabled2Hours, props.disabled1Hour);
    const isPackageItemDisabledForCombination = duration => {
        if (!props.enabled) {
            return true;
        }
        const packagesItemsPerson: PackagesItemsPerson | undefined = getPackageItemForCombination(duration, !!props.withKarting, props.personType);
        return packagesItemsPerson === undefined;
    };

    const visibleOptions = durationOptions.filter(option => !isPackageItemDisabledForCombination(option.value));
    console.log(visibleOptions, props.chosenDuration);
    if (props.showLabels) {
        return (
            <div className={clsx("BookingPackagesTicketsSelectStandard flex w-full", props.className)}>
                <fieldset aria-label={t("Choose a duration of your stay")} className={"w-full"}>
                    <RadioGroup
                        className="flex flex-col xs:flex-row xs:flex-wrap items-end gap-2"
                        value={props.chosenDuration}
                        onChange={props.setChosenDuration}
                    >
                        {visibleOptions.map(option => {
                            const isRecommendationOption = option.value === 270 || option.value === 240;
                            const price = getPackageItemPrice(option.value, props.personType);
                            const isSelected = props.chosenDuration === option.value;

                            return (
                                <div key={option.value} className="flex flex-col grow w-full xs:w-auto sm:min-w-[90px]">
                                    {isRecommendationOption && (
                                        <div
                                            className={
                                                "rounded-t-xl flex flex-row xs:flex-col md:flex-row w-full items-center justify-center gap-1 px-1 py-1.5 uppercase bg-[linear-gradient(90deg,#FFAE00_0%,#E1570B_35%,#D12C11_70%,#C20016_100%)] border-arbo-orange text-white"
                                            }
                                        >
                                            <div className={"flex flex-row items-center gap-2 xs:gap-1"}>
                                                <img
                                                    src="/img/credits/top-recommendation-icon.svg"
                                                    alt="top recommendation"
                                                    className="size-[12px] xs:size-[12px]"
                                                />
                                                <span className={"text-white text-[10px] font-bold leading-none"}>{t("Top")}</span>
                                            </div>
                                            <div
                                                className={"text-white text-[10px] xs:text-[9px] md:text-[10px] font-bold leading-none inline-flex text-center"}
                                            >
                                                <span>{t("recommendation")}</span>
                                            </div>
                                        </div>
                                    )}
                                    <RadioGroup.Option
                                        className={clsx(
                                            props.enabled && option.enabled ? "cursor-pointer focus:outline-none" : "cursor-not-allowed opacity-25",
                                            "min-h-[56px] w-full relative flex flex-row items-center justify-center transition-all duration-150 shadow-sm border-2 gap-2 xs:gap-0 px-2 sm:pl-1",
                                            isRecommendationOption ? "rounded-b-xl border-t-0" : "rounded-xl",
                                            isSelected
                                                ? "border-[#D6A50D] bg-[#D6A50D]/5 text-black"
                                                : "border-[#D9D9D9] hover:border-[#D6A50D] bg-[#FBFBFB] text-black"
                                        )}
                                        disabled={!props.enabled || !option.enabled}
                                        value={option.value}
                                    >
                                        <div className={"border-2 rounded-full bg-white p-0.5 shrink-0"}>
                                            <FeatherIcon
                                                name={"check"}
                                                className={clsx("size-3 xs:size-4 sm:size-5 flex-none ", isSelected ? "text-[#D6A50D]" : "text-transparent")}
                                                strokeWidth={3}
                                            />
                                        </div>
                                        <div
                                            className={
                                                "flex flex-row xs:flex-col w-full xs:w-auto justify-between xs:justify-center items-center xs:px-1 sm:px-2 py-2"
                                            }
                                        >
                                            <span className={"font-bold text-xs sm:text-sm whitespace-nowrap"}>{option.name}</span>
                                            <span className={"font-semibold text-[11px] xs:text-xs text-black"}>{currencyNumberFormat(price)}</span>
                                        </div>
                                    </RadioGroup.Option>
                                </div>
                            );
                        })}
                    </RadioGroup>
                </fieldset>
            </div>
        );
    }

    return (
        <div className={clsx("BookingPackagesTicketsSelectStandard flex w-full", props.className)}>
            <fieldset aria-label={t("Choose a duration of your stay")} className={"w-full sm:w-auto"}>
                <RadioGroup
                    className="flex flex-col items-center space-y-2 sm:space-y-0 w-full sm:w-auto sm:grid sm:grid-cols-3 gap-2 lg:gap-1 xl:gap-2"
                    value={props.chosenDuration}
                    onChange={props.setChosenDuration}
                >
                    {durationOptions.map(option => {
                        const isDisabledFromPackageItem = isPackageItemDisabledForCombination(option.value);

                        if (isDisabledFromPackageItem) {
                            return (
                                <React.Fragment key={option.value}>
                                    <></>
                                </React.Fragment>
                            );
                        }

                        return (
                            <RadioGroup.Option
                                key={option.value}
                                className={clsx(
                                    props.enabled && option.enabled ? "cursor-pointer focus:outline-none" : "cursor-not-allowed opacity-25",
                                    "w-auto min-w-[102px] lg:min-w-[120px] min-h-[50px] h-full relative flex flex-col items-center justify-center rounded-xl transition-all duration-150 shadow-sm px-2 py-1.5",
                                    props.chosenDuration === option.value
                                        ? "border-2 border-[#D6A50D] bg-[#D6A50D]/5 text-[#D6A50D]"
                                        : "border border-[#D9D9D9] hover:border-[#D6A50D] hover:bg-white bg-gray-50 text-transparent"
                                )}
                                disabled={isDisabledFromPackageItem || !props.enabled || !option.enabled}
                                value={option.value}
                                {...(props.chosenDuration == option.value ? {"data-checked": true} : {})}
                            >
                                {({checked}) => (
                                    <FeatherIcon name={"check"} className={clsx("size-6 flex-none", checked ? "opacity-100" : "opacity-0")} strokeWidth={3} />
                                )}
                            </RadioGroup.Option>
                        );
                    })}
                </RadioGroup>
            </fieldset>
        </div>
    );
};

export default BookingPackagesTicketsSelectStandard;
